/* ==========================================================================
   Region seam — New Zealand (layawaydepot.co.nz).

   Loaded before assets/layaway-form.js, which reads window.LAYAWAY_REGION for
   everything that differs from the US baseline. This file makes the shared
   checkout shell behave like the Prestige-era NZ form did:

   - direct debit only: a NZ bank account (bank-branch-account-suffix), checked
     with the bank's own check-digit algorithm and named from the bank prefix;
   - DD/MM/YYYY dates, weekday paydays from tomorrow, NZ phone and postcode;
   - $15 / $25 / $50 a week, weekly or fortnightly, arithmetic from
     assets/layby-plan.js (byte-identical to the order backend's copy);
   - the create-order payload LAYAWAYDEPOTNZ-vercel already reads — same field
     names, same units.

   Nothing here recomputes money on its own: the plan comes from layby-plan.js
   and the order backend re-derives every figure from the items it receives.
   ========================================================================== */
window.LAYAWAY_REGION = (function () {
  'use strict';

  var $ = function (id) { return document.getElementById(id); };
  var digitsOf = function (v) { return String(v == null ? '' : v).replace(/\D/g, ''); };

  /* ------------------------------------------------------ NZ bank accounts
     Carried verbatim from LAYAWAYDEPOTNZ assets/page-form.es6.js: bank and
     branch ranges for every NZ bank prefix and the seven check-digit
     algorithms behind them. An account that fails here is one the bank would
     bounce at the first debit. */
var NZBankValidator = (function () {
  var partMaxLengths = { id: 2, branch: 4, base: 8, suffix: 4 };

  // Bank/branch ranges grouped by checksum algorithm key
  var bankData = [
    {
      key: 'AB',
      branches: {
        '01': [[1,999],[1100,1199],[1800,1899],[6150,6150]],
        '02': [[1,999],[1200,1299],[2025,2055]],
        '03': [[1,999],[1300,1399],[1500,1599],[1700,1799],[1900,1999],[7350,7399]],
        '04': [[2014,2024]],
        '05': [[8884,8889]],
        '06': [[1,999],[1400,1499]],
        '10': [[5165,5169]],
        '11': [[5000,6499],[6600,8999]],
        '12': [[3000,3299],[3400,3499],[3600,3699]],
        '13': [[4900,4999]],
        '14': [[4700,4799]],
        '15': [[3900,3999]],
        '16': [[4400,4499]],
        '17': [[3300,3399]],
        '18': [[3500,3599]],
        '19': [[4600,4649]],
        '20': [[4100,4199]],
        '21': [[4800,4899]],
        '22': [[4000,4049]],
        '23': [[3700,3799]],
        '24': [[4300,4349]],
        '27': [[3800,3849]],
        '30': [[2900,2949]],
        '35': [[2400,2499]],
        '38': [[9000,9499]],
        '88': [[8800,8805]]
      }
    },
    { key: 'D', branches: { '08': [[6500,6599]] } },
    { key: 'F', branches: { '25': [[2500,2599]] } },
    { key: 'X', branches: { '31': [[2800,2849]] } }
  ];

  // Checksum weight tables per algorithm
  var bankChecksums = {
    A: { weighting: [0,0,6,3,7,9,0,0,10,5,8,4,2,1,0,0,0,0], modulo: 11 },
    B: { weighting: [0,0,0,0,0,0,0,0,10,5,8,4,2,1,0,0,0,0], modulo: 11 },
    D: { weighting: [0,0,0,0,0,0,0,7,6,5,4,3,2,1,0,0,0,0], modulo: 11 },
    E: { weighting: [0,0,0,0,0,0,0,0,0,0,5,4,3,2,0,0,0,1], modulo: 11, specialCase: true },
    F: { weighting: [0,0,0,0,0,0,0,1,7,3,1,7,3,1,0,0,0,0], modulo: 10 },
    G: { weighting: [0,0,0,0,0,0,0,1,3,7,1,3,7,1,0,3,7,1], modulo: 10, specialCase: true },
    X: { weighting: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], modulo: 1 }
  };

  function padLeft(input, length) {
    var s = String(input);
    while (s.length < length) s = '0' + s;
    return s;
  }

  function inRanges(value, ranges) {
    for (var i = 0; i < ranges.length; i++) {
      if (value >= ranges[i][0] && value <= ranges[i][1]) return true;
    }
    return false;
  }

  function sumDigits(n) {
    var s = 0;
    var str = String(n);
    for (var i = 0; i < str.length; i++) s += parseInt(str[i], 10);
    return s;
  }

  function getPaddedArray(parts) {
    var arr = [];
    var keys = ['id', 'branch', 'base', 'suffix'];
    for (var k = 0; k < keys.length; k++) {
      var padded = padLeft(parts[keys[k]], partMaxLengths[keys[k]]);
      for (var c = 0; c < padded.length; c++) arr.push(padded[c]);
    }
    return arr; // 18 digits
  }

  function findBankData(id, branch) {
    var paddedId = padLeft(id, partMaxLengths.id);
    var branchNum = parseInt(branch, 10);
    for (var i = 0; i < bankData.length; i++) {
      var ranges = bankData[i].branches[paddedId];
      if (ranges && inRanges(branchNum, ranges)) return bankData[i];
    }
    return null;
  }

  function getChecksum(bankEntry, base) {
    var key = bankEntry.key;
    if (key === 'AB') {
      key = parseInt(base, 10) < 990000 ? 'A' : 'B';
    }
    return bankChecksums[key] || null;
  }

  /**
   * Run modulus checksum on a parts object. Returns true if valid.
   */
  function runChecksum(parts, bankEntry) {
    var algorithm = getChecksum(bankEntry, parts.base);
    if (!algorithm) return false;

    var digits = getPaddedArray(parts);
    var earlyExit = !algorithm.specialCase;
    var total = 0;

    for (var i = 0; i < digits.length; i++) {
      var multiplied = parseInt(digits[i], 10) * algorithm.weighting[i];
      if (earlyExit || multiplied < 10) {
        total += multiplied;
      } else {
        var summed = sumDigits(multiplied);
        total += summed < 10 ? summed : sumDigits(summed);
      }
    }

    return total % algorithm.modulo === 0;
  }

  /**
   * Validate an NZ bank account number.
   * @param {string|object} input — "BB-bbbb-AAAAAAA-SSS" string or { id, branch, base, suffix }
   * @returns {{ valid: boolean, error: string|null }}
   */
  function validate(input) {
    var parts;

    if (typeof input === 'object' && input !== null && 'id' in input) {
      parts = input;
    } else if (typeof input === 'string') {
      var segs = input.split(/[^0-9]/);
      if (segs.length !== 4) return { valid: false, error: 'Account number must have 4 parts (bank-branch-account-suffix)' };
      parts = { id: segs[0], branch: segs[1], base: segs[2], suffix: segs[3] };
    } else {
      return { valid: false, error: 'Invalid input' };
    }

    // Check all parts are numeric and within max lengths
    var keys = ['id', 'branch', 'base', 'suffix'];
    for (var k = 0; k < keys.length; k++) {
      var val = parts[keys[k]];
      if (!val || !/^[0-9]+$/.test(val) || val.length > partMaxLengths[keys[k]]) {
        return { valid: false, error: 'Each part of the account number must be numeric' };
      }
    }

    // Look up bank + branch
    var bankEntry = findBankData(parts.id, parts.branch);
    if (!bankEntry) {
      return { valid: false, error: 'Bank or branch number not recognised' };
    }

    // Run modulus check
    if (runChecksum(parts, bankEntry)) {
      return { valid: true, error: null };
    }

    // NZ accounts can be 7-digit base + 3-digit suffix or 8-digit base + 2-digit suffix.
    // Customers sometimes split them wrong (e.g. putting the suffix's leading 0 into the base).
    // If the initial check failed, try shifting a digit between base and suffix.
    if (parts.base.length === 8 && parts.suffix.length <= 3) {
      var altParts = { id: parts.id, branch: parts.branch, base: parts.base.slice(0, 7), suffix: parts.base.slice(7) + parts.suffix };
      if (altParts.suffix.length <= 4 && runChecksum(altParts, bankEntry)) {
        return { valid: true, error: null };
      }
    }
    if (parts.base.length <= 7 && parts.suffix.length >= 2) {
      var altParts2 = { id: parts.id, branch: parts.branch, base: parts.base + parts.suffix.charAt(0), suffix: parts.suffix.slice(1) };
      if (altParts2.base.length <= 8 && altParts2.suffix.length >= 1 && runChecksum(altParts2, bankEntry)) {
        return { valid: true, error: null };
      }
    }

    return { valid: false, error: 'Account number failed validation check' };
  }

  // Bank ID → display name mapping
  var bankNames = {
    '01': 'ANZ', '02': 'BNZ', '03': 'Westpac', '04': 'ANZ',
    '05': 'China Construction Bank', '06': 'ANZ', '08': 'BNZ',
    '10': 'ANZ', '11': 'ANZ / Co-operative Bank', '12': 'ASB',
    '13': 'ANZ', '14': 'ANZ', '15': 'TSB', '16': 'ANZ',
    '17': 'ANZ', '18': 'ANZ', '19': 'ANZ', '20': 'ANZ',
    '21': 'ANZ', '22': 'ANZ', '23': 'ANZ', '24': 'ANZ',
    '25': 'ANZ', '27': 'ANZ', '30': 'ICBC', '31': 'HSBC / Citibank',
    '35': 'ANZ', '38': 'Kiwibank', '88': 'Bank of China'
  };

  function getBankName(id) {
    var paddedId = padLeft(id, partMaxLengths.id);
    return bankNames[paddedId] || null;
  }

  return { validate: validate, getBankName: getBankName };
})();
  window.NZBankValidator = NZBankValidator;

  /** "02-0123-0123456-000" from whatever was typed; the hidden field carries this. */
  function formatNz(raw) {
    var d = digitsOf(raw).slice(0, 16), out = '';
    if (d.length > 0) out += d.substring(0, 2);
    if (d.length > 2) out += '-' + d.substring(2, 6);
    if (d.length > 6) out += '-' + d.substring(6, 13);
    if (d.length > 13) out += '-' + d.substring(13, 16);
    return out;
  }

  function affiliateCode() {
    try {
      var p = new URLSearchParams(window.location.search);
      var fromUrl = (p.get('affiliate') || p.get('ref_code') || '').trim();
      if (fromUrl) { try { localStorage.setItem('layaway_affiliate_referral_code', fromUrl); } catch (e) {} return fromUrl; }
      return (localStorage.getItem('layaway_affiliate_referral_code') || localStorage.getItem('layaway_affiliate_code') || '').trim();
    } catch (e) { return ''; }
  }

  function frequencyWord(key) { return key === 'biweekly' ? 'Fortnightly' : 'Weekly'; }

  function bindBank(H) {
    var input = $('account-number-input');
    if (!input) return;
    var hidden = $('account-number'), bankHidden = $('bank-name'), nameEl = $('detected-bank-name'), iconEl = $('bsb-status-icon');
    var setState = function (text, cls) {
      if (nameEl) { nameEl.textContent = text || ''; nameEl.className = 'bsb-bank-name' + (cls ? ' ' + cls : ''); }
      if (iconEl) iconEl.className = 'bsb-status-icon' + (cls ? ' ' + cls : '');
    };
    input.addEventListener('input', function () {
      var formatted = formatNz(input.value);
      if (formatted !== input.value) input.value = formatted;
      if (hidden) hidden.value = formatted;
      H.setError('account-number-input', '');
      var bankId = digitsOf(formatted).substring(0, 2);
      if (bankId.length === 2) {
        var name = NZBankValidator.getBankName(bankId);
        if (name) { setState(name, 'verified'); if (bankHidden) bankHidden.value = name; }
        else { setState('Bank not recognised', 'invalid'); if (bankHidden) bankHidden.value = ''; }
      } else {
        setState('', ''); if (bankHidden) bankHidden.value = '';
      }
    });
    if (input.value) input.dispatchEvent(new Event('input'));
  }

  function bindEmployment(H) {
    var row = $('employment-selector'), hidden = $('order-form-employment-status');
    if (!row || !hidden) return;
    row.addEventListener('click', function (e) {
      var chip = e.target.closest('.layaway-chip');
      if (!chip || chip.dataset.value === undefined) return;
      row.querySelectorAll('.layaway-chip').forEach(function (c) { c.classList.remove('is-selected'); });
      chip.classList.add('is-selected');
      hidden.value = chip.dataset.value;
      H.setError('order-form-employment-status', '');
      H.persist();
    });
    if (hidden.value) row.querySelectorAll('.layaway-chip').forEach(function (c) {
      c.classList.toggle('is-selected', c.dataset.value === hidden.value);
    });
  }

  /* Shell markup that is worded for the US and has no region hook of its own. */
  function localiseShell() {
    var phone = $('order-form-register-phone');
    if (phone) phone.placeholder = 'e.g. 021 234 5678';
    var dob = $('dob');
    if (dob) dob.placeholder = 'DD/MM/YYYY';
    var sms = $('sms-consent');
    if (sms) { var wrap = sms.closest('.layaway-checkbox'); if (wrap) wrap.hidden = true; }
  }

  return {
    code: 'nz',
    submittingLabel: 'Starting your layby…',
    fortnightWord: 'a fortnight',
    rail: 'direct_debit',
    dateOrder: 'DMY',
    cadences: {
      weekly: { days: 7, label: 'every week' },
      biweekly: { days: 14, label: 'every fortnight' }
    },
    // Debits run Monday to Friday; the bank file needs a day's lead time, so the
    // first offered payday is tomorrow and the chips cover the next two weeks.
    payableDay: function (d) { var g = d.getDay(); return g >= 1 && g <= 5; },
    payableDayHint: 'Payments come out on a weekday.',
    firstPaydayOffsetDays: 1,
    horizonDays: 13,
    phone: {
      // NZ mobiles 02x (9–11 digits incl. the 0), landlines 0[3-9] (9 digits); +64 accepted.
      test: function (v) {
        var d = digitsOf(v);
        if (d.indexOf('64') === 0 && d.length >= 10) d = '0' + d.slice(2);
        return /^0(2\d{7,9}|[3-9]\d{7})$/.test(d);
      },
      hint: 'Enter a valid New Zealand phone number, e.g. 021 234 5678.',
      mask: function (v) {
        var d = v.replace(/\D/g, '').slice(0, 11);
        if (d.length > 6) return d.slice(0, 3) + ' ' + d.slice(3, 6) + ' ' + d.slice(6);
        if (d.length > 3) return d.slice(0, 3) + ' ' + d.slice(3);
        return d;
      }
    },
    postcode: {
      test: function (v) { return /^\d{4}$/.test(v); },
      hint: 'Enter your 4-digit postcode.',
      mask: function (v) { return v.replace(/\D/g, '').slice(0, 4); }
    },
    dob: { hint: 'Use DD/MM/YYYY.' },
    address: { allowedStateCodes: null, stateHint: 'Choose your region.', placesCountry: 'nz' },
    quoteTax: false,
    timeline: {
      shipWhen: 'Delivered free',
      shipNote: 'We order it after your final payment and deliver it free, anywhere in New Zealand.',
      homeWhen: 'At your door',
      homeNote: 'Fully paid. Nothing more to pay, ever.'
    },

    saveCartPayload: function (body) {
      // LAYAWAYDEPOTNZ-vercel save-cart reads the contact fields at the top level.
      return {
        token: body.token, site: body.site,
        email: body.customer.email, phone: body.customer.phone,
        first_name: body.customer.first_name, last_name: body.customer.last_name,
        items: body.items, step: body.step, formDetails: body.formDetails
      };
    },

    step4Fields: ['order-form-employment-status', 'account-number-input', 'signature-pad', 'agreed_to_terms', 'agreed_to_cancellation'],
    errorMap: {
      'account-number-input': 'bank-error',
      'signature-pad': 'sigpad-error',
      'order-form-employment-status': 'employment-error'
    },
    validators: {
      'account-number-input': function (v) {
        if (!v) return 'Enter the bank account number the payments come from.';
        var r = NZBankValidator.validate(v);
        if (!r.valid) return r.error || 'That account number does not look right.';
        if (!$('bank-name').value) return 'We don\'t recognise that bank prefix — check the first two digits.';
        return '';
      },
      'signature-pad': function (v, el, H) { return H.padHasInk() ? '' : 'Please sign in the box above.'; },
      'order-form-employment-status': function (v) { return v ? '' : 'Pick the option that fits best.'; },
      'agreed_to_terms': function (v, el) { return el.checked ? '' : 'Please check this box to continue.'; },
      'agreed_to_cancellation': function (v, el) { return el.checked ? '' : 'Please check this box to continue.'; }
    },

    plan: function (ctx) {
      var P = window.LaybyPlan;
      if (!P || !ctx.rate_cents) return null;
      var p = P.laybyPlan(ctx.financed_cents, ctx.rate_cents, frequencyWord(ctx.frequency));
      return { installment_cents: p.instalmentCents, count: p.count, final_cents: p.finalCents };
    },

    buildPayload: function (H) {
      var v = H.v, checked = H.checked;
      var cart = window.cartData || {};
      var extras = window.itemTotalValues || {};
      var rate = H.rateCents();
      // Order generation and its promo gates consume individual unit values.
      // Shopify final_line_price already includes quantity and line discounts.
      // Expand here so both pricing and gift thresholds count every unit once.
      // Distribute remainder cents rather than rounding away a line discount.
      var items = (cart.items || []).reduce(function (result, it) {
        var extra = extras[it.id] || extras[it.variant_id] || {};
        var lineCents = it.final_line_price != null ? it.final_line_price : extra.total_value_cents;
        var quantity = it.quantity || 1;
        var unitCents = Math.floor(lineCents / quantity);
        var remainder = lineCents % quantity;
        for (var unit = 0; unit < quantity; unit++) {
          var priceCents = unitCents + (unit < remainder ? 1 : 0);
          result.push({
            variant_id: it.variant_id || it.id,
            product_id: it.product_id,
            title: it.product_title || it.title,
            handle: extra.handle || it.handle || '',
            image: it.image,
            price: rate,
            total_value: priceCents,
            weeks: Math.max(1, Math.ceil(priceCents / rate)),
            quantity: 1,
            tags: extra.tags || []
          });
        }
        return result;
      }, []);
      var pad = H.sigPad();
      return {
        site: H.CFG.SITE || 'nz',
        customer: {
          first_name: v('order-form-register-f-name'),
          last_name: v('order-form-register-l-name'),
          email: v('order-form-register-email'),
          phone: v('order-form-register-phone').replace(/[-\s]/g, ''),
          address: v('order-form-register-street-address'),
          address2: v('order-form-register-street-address-2'),
          city: v('order-form-register-city'),
          state: v('province'),
          postcode: v('order-form-register-zip')
        },
        items: items,
        formDetails: {
          date_of_birth: v('dob'),
          gender: '',
          next_pay_date: v('next-pay'),
          employment_status: v('order-form-employment-status'),
          pay_frequency: frequencyWord(H.state.frequency),
          bsb_number: '',
          account_number: v('account-number'),
          bank_name: v('bank-name'),
          reference_first_name: '',
          reference_last_name: '',
          reference_phone: '',
          promo_code: v('promo-code').toUpperCase(),
          affiliate_referral_code: affiliateCode(),
          attribution: window.ldAttribution || H.attribution(),
          agreed_to_terms: checked('agreed_to_terms'),
          agreed_to_charges: false,
          agreed_to_cancellation: checked('agreed_to_cancellation'),
          agreed_to_payment_info: false,
          confirmed_affordability: false,
          subscribe_to_mailing: checked('order-subscribe-to-mailing-list'),
          is_bundle: items.length > 1,
          bundle_item_count: String(items.length),
          signature: (pad && pad.hasInk()) ? pad.dataUrl() : '',
          submitted_at: new Date().toISOString(),
          payment_method: 'direct_debit',
          stripe_setup_intent_id: ''
        },
        weekly_payment_cents: rate,
        fbp: H.cookie('_fbp'),
        fbc: H.cookie('_fbc'),
        event_id: window.fbqEventId || (window.__metaGenId ? window.__metaGenId('purchase') : 'lw-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8))
      };
    },

    beforePurchaseTrack: function (payload) {
      if (typeof window.fbq !== 'function') return;
      var c = payload.customer || {};
      var ph = window.__metaPhoneHash ? window.__metaPhoneHash(c.phone) : digitsOf(c.phone);
      window.fbq('init', '2196737243978336', {
        em: (c.email || '').toLowerCase(), fn: (c.first_name || '').toLowerCase(), ln: (c.last_name || '').toLowerCase(),
        ph: ph, ct: (c.city || '').toLowerCase(), st: (c.state || '').toLowerCase(), zp: c.postcode || '', country: 'nz'
      });
    },

    init: function (H) {
      localiseShell();
      bindBank(H);
      bindEmployment(H);
      H.renderSchedule();
    }
  };
})();
